import { useEffect, useMemo, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { useOnline } from '@/lib/useOnline';
import { enqueue, flushQueue } from '@/lib/offline';
import { Spinner, CarryoverChip } from '@/components/Common';
import { TEST_IDS } from '@/constants/ids';
import {
  CheckCircleIcon,
  WarningIcon,
  XCircleIcon,
  SparkleIcon,
} from '@phosphor-icons/react';

const STATUS = [
  { key: 'present_current', label: 'Present & Current', cls: 'gl-chip-status-present', icon: CheckCircleIcon },
  { key: 'outdated', label: 'Incomplete / Outdated', cls: 'gl-chip-status-outdated', icon: WarningIcon },
  { key: 'missing', label: 'Missing', cls: 'gl-chip-status-missing', icon: XCircleIcon },
  { key: 'na', label: 'N/A', cls: 'gl-chip gl-chip-neutral', icon: XCircleIcon },
];

export default function ChecklistTab({ eng }) {
  const [items, setItems] = useState(null);
  const online = useOnline();
  const [score, setScore] = useState(null);
  const [topGaps, setTopGaps] = useState(null);
  const [topGapsBusy, setTopGapsBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'missing' | 'outdated' | 'present_current' | 'na'

  async function load() {
    try {
      const { data } = await api.get(`/engagements/${eng.id}/gap_items`);
      setItems(data);
      const sr = await api.get(`/engagements/${eng.id}/compliance_score`);
      setScore(sr.data);
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
    }
  }
  useEffect(() => {
    load();
  }, [eng.id]);

  async function update(item, patch) {
    const updated = { ...item, ...patch };
    setItems((arr) => arr.map((x) => (x.id === item.id ? updated : x)));
    if (!online) {
      await enqueue({ type: 'update_gap_item', item_id: item.id, payload: patch });
    } else {
      try {
        await api.patch(`/gap_items/${item.id}`, patch);
        // refresh score
        const sr = await api.get(`/engagements/${eng.id}/compliance_score`);
        setScore(sr.data);
        flushQueue();
      } catch (e) {
        toast.error(formatApiError(e));
        load();
      }
    }
  }

  async function runTopGaps() {
    setTopGapsBusy(true);
    setTopGaps(null);
    try {
      const { data } = await api.get(`/engagements/${eng.id}/top_gaps`);
      setTopGaps(data.top_critical || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTopGapsBusy(false);
    }
  }

  const groups = useMemo(() => {
    if (!items) return [];
    const m = new Map();
    for (const it of items) {
      if (!m.has(it.category)) m.set(it.category, []);
      m.get(it.category).push(it);
    }
    return Array.from(m.entries()).map(([cat, list]) => ({ cat, list }));
  }, [items]);

  async function bulkMarkCategory(category, status) {
    const cnt = items.filter((it) => it.category === category).length;
    if (!window.confirm(`Mark all ${cnt} items in "${category}" as ${status === 'na' ? 'N/A' : status}?\nThis cannot be undone in bulk.`)) return;
    try {
      await api.post(`/engagements/${eng.id}/gap_items/bulk`, { category, status });
      toast.success(`${cnt} items marked`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (!items) return <Spinner label="Loading checklist…" />;

  const total = items.length;
  const counts = items.reduce(
    (acc, x) => {
      acc[x.status] = (acc[x.status] || 0) + 1;
      return acc;
    },
    { present_current: 0, outdated: 0, missing: 0 }
  );

  const pct = score?.score ?? 0;
  const scoreColor = pct >= 80 ? 'var(--gl-p3)' : pct >= 50 ? 'var(--gl-p2)' : 'var(--gl-p1)';

  return (
    <div>
      {/* Score header */}
      <div className="gl-card gl-card-accent mb-5">
        <div className="flex items-end gap-6 flex-wrap">
          <div>
            <div className="gl-label">Compliance Score</div>
            <div className="font-display font-black leading-none text-6xl md:text-7xl" style={{ color: scoreColor }}>
              {pct.toFixed(0)}<span className="text-3xl align-top">%</span>
            </div>
            <div className="text-xs uppercase tracking-widest mt-1" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter' }}>
              Across {total} documentation items
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS.map((s) => (
              <span key={s.key} className={`gl-chip ${s.cls}`} style={{ padding: '0.65rem 1rem', fontSize: '0.85rem' }}>
                {s.label} · {counts[s.key]}
              </span>
            ))}
          </div>
          <button
            className="gl-btn gl-btn-accent ml-auto"
            onClick={runTopGaps}
            disabled={!online || topGapsBusy}
            data-testid="top-gaps-btn"
          >
            <SparkleIcon size={18} weight="fill" /> {topGapsBusy ? 'Analyzing…' : 'Top Critical Gaps'}
          </button>
        </div>
        {topGaps && topGaps.length > 0 && (
          <div className="mt-4 p-3 rounded-sm border-2" style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3' }}>
            <div className="gl-label" style={{ color: 'var(--gl-dark)' }}>Top critical gaps</div>
            <ol className="list-decimal pl-5 text-sm space-y-1">
              {topGaps.map((g, i) => (
                <li key={i}><b>{g.item}</b> — {g.why}</li>
              ))}
            </ol>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <div className="text-xs uppercase tracking-widest font-bold mr-1" style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
          Filter:
        </div>
        {[
          { key: 'all', label: 'All' },
          { key: 'missing', label: 'Missing only' },
          { key: 'outdated', label: 'Outdated only' },
          { key: 'present_current', label: 'Present only' },
          { key: 'na', label: 'N/A only' },
        ].map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setStatusFilter(s.key)}
            className="px-2 h-8 border-2 rounded-sm text-xs font-bold uppercase tracking-wider"
            style={{
              borderColor: statusFilter === s.key ? 'var(--gl-navy)' : '#d1d5db',
              background: statusFilter === s.key ? 'var(--gl-navy)' : '#fff',
              color: statusFilter === s.key ? '#fff' : 'var(--gl-dark)',
              fontFamily: 'Inter',
              letterSpacing: '0.08em',
            }}
            data-testid={`checklist-filter-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="space-y-5">
        {groups.map(({ cat, list }) => {
          const isConditional = list[0]?.is_conditional;
          const note = list[0]?.category_note;
          const reviewedCount = list.filter((it) => it.status === 'present_current' || it.status === 'outdated' || it.status === 'na').length;
          const missingCount = list.filter((it) => it.status === 'missing').length;
          const visible = statusFilter === 'all' ? list : list.filter((it) => it.status === statusFilter);
          if (visible.length === 0 && statusFilter !== 'all') return null;
          return (
            <div key={cat} className="gl-card">
              <div
                className="px-3 py-2 -mx-4 -mt-4 mb-3 font-display font-extrabold uppercase tracking-widest flex items-center gap-2 flex-wrap"
                style={{ background: 'var(--gl-dark-navy)', color: 'var(--gl-gold)' }}
              >
                <span>{cat}</span>
                <span
                  className="text-xs font-bold uppercase px-2 py-0.5"
                  style={{ background: reviewedCount === list.length ? 'var(--gl-p3)' : 'var(--gl-gold)',
                           color: reviewedCount === list.length ? '#fff' : 'var(--gl-dark)',
                           letterSpacing: '0.08em' }}
                  data-testid={`category-progress-${cat.replace(/\W+/g, '-').toLowerCase()}`}
                >
                  {reviewedCount}/{list.length} reviewed
                  {missingCount > 0 && ` · ${missingCount} missing`}
                </span>
                <button
                  type="button"
                  onClick={() => bulkMarkCategory(cat, 'na')}
                  className="ml-auto text-xs font-bold uppercase px-2 py-0.5 hover:opacity-80"
                  style={{ background: 'transparent', color: 'var(--gl-gold)', border: '1px solid var(--gl-gold)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
                  title={`Mark all ${list.length} items in this category as N/A`}
                  data-testid={`bulk-na-${cat.replace(/\W+/g, '-').toLowerCase()}`}
                >
                  Mark all N/A
                </button>
                {isConditional && (
                  <span
                    className="gl-chip"
                    style={{ background: 'var(--gl-gold)', color: 'var(--gl-dark)', padding: '0.25rem 0.5rem', fontSize: '0.65rem' }}
                  >
                    CONDITIONAL
                  </span>
                )}
              </div>
              {isConditional && note && (
                <div
                  className="mb-3 p-3 border-l-4 text-sm"
                  style={{ borderColor: 'var(--gl-gold)', background: '#fdf6e3', color: 'var(--gl-dark)' }}
                  data-testid={`category-note-${cat.replace(/\W+/g, '-').toLowerCase()}`}
                >
                  {note}
                </div>
              )}
              <div className="space-y-2">
                {visible.map((it) => (
                  <GapRow key={it.id} item={it} onUpdate={(patch) => update(it, patch)} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GapRow({ item, onUpdate }) {
  const [notes, setNotes] = useState(item.notes || '');
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 p-3 border-l-4 rounded-sm"
      style={{
        borderColor:
          item.status === 'present_current' ? 'var(--gl-p3)' : item.status === 'outdated' ? 'var(--gl-p2)' : item.status === 'na' ? 'var(--gl-muted)' : 'var(--gl-p1)',
        background: '#fbfbfb',
      }}
      data-testid={TEST_IDS.gapItemRow(item.id)}
    >
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold" style={{ color: 'var(--gl-dark)' }}>{item.item_label}</span>
          {item.is_carryover && <CarryoverChip />}
        </div>
        {item.cfr_citation && (
          <div
            className="text-xs uppercase tracking-widest font-bold mt-0.5"
            style={{ color: 'var(--gl-navy)', fontFamily: 'Inter', letterSpacing: '0.1em' }}
            data-testid={`gap-cfr-${item.id}`}
          >
            {item.cfr_citation}
          </div>
        )}
        {(item.status !== 'present_current' && item.status !== 'na') && (
          <textarea
            className="gl-input gl-textarea mt-2"
            rows={2}
            placeholder="Notes / what's missing / next step…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={() => notes !== item.notes && onUpdate({ notes })}
            data-testid={TEST_IDS.gapNotes(item.id)}
          />
        )}
      </div>
      <div className="flex md:flex-col flex-row gap-2 items-start">
        {STATUS.map((s) => {
          const active = item.status === s.key;
          const tid =
            s.key === 'present_current'
              ? TEST_IDS.gapStatusPresent(item.id)
              : s.key === 'outdated'
                ? TEST_IDS.gapStatusOutdated(item.id)
                : s.key === 'na'
                  ? `gap-status-na-${item.id}`
                  : TEST_IDS.gapStatusMissing(item.id);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => onUpdate({ status: s.key })}
              className={`gl-chip ${s.cls}`}
              style={{ opacity: active ? 1 : 0.4, padding: '0.55rem 0.75rem', fontSize: '0.7rem' }}
              data-testid={tid}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <div className="flex md:flex-col gap-1">
        {['P1', 'P2', 'P3'].map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onUpdate({ priority: p })}
            className={`gl-chip ${p === 'P1' ? 'gl-chip-p1' : p === 'P2' ? 'gl-chip-p2' : 'gl-chip-p3'}`}
            style={{ opacity: item.priority === p ? 1 : 0.4, padding: '0.55rem 0.6rem' }}
            data-testid={`gap-priority-${item.id}-${p}`}
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}
